// Sign-in (04-sign-in.md): the work email, finding its company, connecting to its chat server, and
// the company's step — a password server, single sign-on, a sign-in token, or the company form
// with its code and error steps. English only for now; one code field (the owner's choice).
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { Icon, icons } from '../blocks/icons';
import { Reference } from '../blocks/Wait';
import { describe } from '../core/errors';
import type { CompanyEntry, Config } from '../core/config';
import { t } from '../i18n';
import { Link, navigate } from '../core/router';
import type { Session } from '../core/session';
import { chooseCompany, chosenCompany, companyEntry, defaultServer, discover, discoveryOn, domainOf, isKnownServer, lastEmail, notSetUp, rememberEmail } from './company';
import { ErrorLine, Field, Frame, Heading, MainButton, Mark, useWait } from './parts';
import { adding, cancelAdding } from '../accounts/store';
import { Button } from '../blocks/Button';
import { adoptPending } from '../core/mail';
import { clearEnded, companySignIn, endUntypedToken, pageRequest, resetPageGuard, startPage, takePasswordNote, usesPage } from './account';
import { signOutOfCompany } from '../session/admin';
import { identifier, login, loginFlows, loginWords, reach, reachWords, SignInError, ssoUrl, type ReachFailure } from './server';

type Step =
  | { name: 'starting' }
  | { name: 'not-set-up' }
  | { name: 'email'; error?: ReactNode }
  | { name: 'connecting'; server: string; line: string; failure?: string }
  | { name: 'password'; server: string; base: string }
  | { name: 'sso'; server: string; base: string }
  | { name: 'token'; server: string; base: string; failure?: string }
  | { name: 'company'; server: string; base: string; entry: CompanyEntry; email: string }
  | { name: 'code'; server: string; base: string; entry: CompanyEntry; email: string; password: string }
  | { name: 'error'; server: string; base: string; entry: CompanyEntry; email: string; reference?: string }
  | { name: 'nothing' };

const PILOT = 'https://desyr.chat/#pilot';
const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A step to the next, with the View Transition where there is one (04, Motion). */
function transition(update: () => void, back = false) {
  if (!document.startViewTransition || reduced()) return update();
  document.documentElement.dataset.siBack = back ? 'true' : 'false';
  const t = document.startViewTransition(() => flushSync(update));
  // A newer step skips this one's animation; that is no failure.
  for (const p of [t.ready, t.finished, t.updateCallbackDone]) p.catch(() => {});
}


export function SignIn({ config, server: deepServer, onSignedIn }: { config: Config; server?: string; onSignedIn: (s: Session) => void }) {
  const [step, setStepNow] = useState<Step>({ name: 'starting' });
  // A transition's step lands a moment later; a step set meanwhile wins over it.
  const asked = useRef(0);
  const setStep = (next: Step | ((s: Step) => Step)) => {
    if (typeof next !== 'function') asked.current++;
    setStepNow(next);
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => setError(undefined), [step.name]);
  const waiting = useWait(busy || step.name === 'connecting' || step.name === 'starting');
  const card = useRef<HTMLDivElement>(null);
  const go = (next: Step, back = false) => {
    const mine = ++asked.current;
    transition(() => mine === asked.current && setStepNow(next), back);
  };
  const done = (s: Session) => {
    chooseCompany(undefined);
    adoptPending(s.user_id);
    onSignedIn(s);
  };

  /** Reaches a company's chat server, then shows its step (04, "Connecting to the company"). */
  const connect = async (server: string) => {
    go({ name: 'connecting', server, line: '' });
    const slow = setTimeout(() => setStep((s) => (s.name === 'connecting' && !s.failure ? { ...s, failure: t('wait.tooSlow') } : s)), 25_000);
    const lineSoon = setTimeout(() => setStep((s) => (s.name === 'connecting' && !s.line ? { ...s, line: t('signin.connecting') } : s)), 1000);
    try {
      const base = await reach(server);
      setStep((s) => (s.name === 'connecting' ? { ...s, line: t('signin.connectingCompany') } : s));
      const flows = await loginFlows(base);
      const token = new URLSearchParams(location.search).get('loginToken');
      const entry = companyEntry(config, server);
      if (token && flows.includes('m.login.token')) {
        history.replaceState(null, '', location.pathname); // once per token
        if (entry && usesPage(entry) && !takePasswordNote()) {
          // Someone else's leftover session at the account service: ended, never used (04, page way 4).
          const fresh = await endUntypedToken(base, token, (s) => signOutOfCompany(base, s.user_id, s.access_token));
          // A second one within 30 s: the error line; its "Try again" starts anyway.
          if (!fresh) return resetPageGuard(), setStep({ name: 'connecting', server, line: '', failure: t('signin.failedMoment') });
          await clearEnded(entry);
          resetPageGuard();
          return startPage(entry);
        }
        go({ name: 'token', server, base });
        signInWithToken(base, server, token);
      } else if (entry && usesPage(entry) && !pageRequest(server)) {
        setStep({ name: 'connecting', server, line: t('signin.connectingCompany') });
        startPage(entry); // the account service's page way: it comes back with its request
      } else if (entry) go({ name: 'company', server, base, entry, email: lastEmail() });
      else if (flows.includes('m.login.password')) go({ name: 'password', server, base });
      else if (flows.includes('m.login.sso') || flows.includes('m.login.cas')) go({ name: 'sso', server, base });
      else go({ name: 'nothing' });
    } catch (e) {
      const reason = (e instanceof SignInError ? e.reason : 'unreachable') as ReachFailure;
      setStep({ name: 'connecting', server, line: '', failure: reachWords(reason) });
    } finally {
      clearTimeout(slow);
      clearTimeout(lineSoon);
    }
  };

  const signInWithToken = (base: string, server: string, token: string) =>
    login(base, { type: 'm.login.token', token }, done).then(done, (e) =>
      setStep({ name: 'token', server, base, failure: loginWords(e, true) }),
    );

  // Where this page starts: the deep link's server, the company this tab chose, or the email.
  useEffect(() => {
    (async () => {
      if (notSetUp(config)) return setStep({ name: 'not-set-up' });
      if (deepServer) {
        if (isKnownServer(config, deepServer)) return connect(deepServer);
        const asDomain = domainOf(`x@${deepServer}`);
        const found = asDomain ? await discover(config, asDomain) : undefined;
        if (found?.kind === 'found') return connect(found.server);
        navigate('/login', { replace: true });
        return setStep({ name: 'email' });
      }
      const chosen = chosenCompany() ?? (discoveryOn(config) ? undefined : defaultServer(config));
      if (chosen) return connect(chosen);
      setStep({ name: 'email' });
    })();
  }, [config, deepServer]);

  useEffect(() => {
    document.title = t('signin.tabTitle');
    return () => void (document.title = 'desyr');
  });

  // On a new step the focus moves to its first field, or to its heading.
  useEffect(() => {
    if (!card.current) return;
    const first = (selector: string) => card.current!.querySelector<HTMLElement>(selector);
    (first('input:not([hidden]):not([readonly])') ?? first('h1'))?.focus();
  }, [step.name]);

  const anotherEmail = discoveryOn(config) ? (
    <Link
      className="si-link si-under"
      to="/login"
      replace
      onClick={() => {
        chooseCompany(undefined);
        go({ name: 'email' }, true);
      }}
    >
      {t('signin.anotherEmail')}
    </Link>
  ) : null;

  const submitEmail = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const email = String(new FormData(e.currentTarget).get('email') ?? '').trim();
    const domain = domainOf(email);
    const askAgain = t('signin.notAnAddress');
    if (!domain) return setStep({ name: 'email', error: askAgain });
    setBusy(true);
    const found = await discover(config, domain);
    setBusy(false);
    let server: string | undefined;
    switch (found.kind) {
      case 'found':
        server = found.server;
        break;
      case 'unknown':
        return setStep({
          name: 'email',
          error: (
            <>
              {t('signin.unknownCompany')}{' '}
              <a href={PILOT} target="_blank" rel="noreferrer" className="si-link">
                {t('signin.pilot')}
              </a>
            </>
          ),
        });
      case 'bad':
        return setStep({ name: 'email', error: askAgain });
      case 'busy':
        return setStep({ name: 'email', error: t('signin.busyMinutes', { n: found.minutes }) });
      case 'down':
        return setStep({ name: 'email', error: t('signin.serviceDown') });
      case 'none':
        server = defaultServer(config);
        if (!server) return setStep({ name: 'email', error: t('signin.notPossible') });
    }
    rememberEmail(email);
    chooseCompany(server);
    await connect(server);
  };

  const submitPassword = async (e: FormEvent<HTMLFormElement>, s: Extract<Step, { name: 'password' }>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const username = form.elements.namedItem('username') as HTMLInputElement;
    const password = form.elements.namedItem('password') as HTMLInputElement;
    if (!username.value.trim()) return username.focus();
    if (!password.value) return password.focus();
    setBusy(true);
    try {
      done(await login(s.base, { type: 'm.login.password', identifier: identifier(username.value.trim()), password: password.value }, done));
    } catch (err) {
      setBusy(false);
      setError(loginWords(err));
    }
  };

  const submitCompany = async (e: FormEvent<HTMLFormElement>, s: Extract<Step, { name: 'company' | 'code' }>) => {
    e.preventDefault();
    const field = e.currentTarget.elements.namedItem(s.name === 'code' ? 'code' : 'password') as HTMLInputElement;
    const code = s.name === 'code' ? field.value.replace(/\s/g, '') : undefined;
    const password = s.name === 'code' ? s.password : field.value;
    if (s.name === 'code' && !/^\d{6}$/.test(code!)) return setError(t('signin.sixDigits'));
    if (s.name === 'company' && !password) return setError(t('signin.emptyPassword'));
    setBusy(true);
    setError(undefined);
    const failed = (e: unknown) =>
      go({ name: 'error', server: s.server, base: s.base, entry: s.entry, email: s.email, reference: describe(e, 'start').reference });
    try {
      const result = await companySignIn({ server: s.server, entry: s.entry, email: s.email, password, code });
      if (result.kind === 'away') return; // the account service's page; it comes back with the token
      setBusy(false);
      if (result.kind === 'code') return go({ ...s, name: 'code', password });
      if (result.kind === 'refused') {
        setError(t(code ? 'signin.wrongCode' : 'signin.wrongPassword'));
        field.value = code ? '' : field.value;
        field.focus();
        field.select();
        return;
      }
      setBusy(true);
      done(await login(s.base, { type: 'm.login.token', token: result.token }, done));
    } catch (e) {
      setBusy(false);
      if (e instanceof SignInError && e.reason === 'leftover') return setError(t('signin.failedMoment'));
      failed(e);
    }
  };

  const forgot = (server: string) => (
    <Link className="si-link" to={`/recover/${encodeURIComponent(server)}/`}>
      {t('signin.forgot')}
    </Link>
  );

  let content: ReactNode;
  let hook: string = step.name;
  switch (step.name) {
    case 'starting':
      hook = 'connecting';
      content = null;
      break;
    case 'not-set-up':
      content = (
        <div role="status" data-desyr-not-set-up="">
          <Heading sub={t('signin.notSetUpLine')}>{t('signin.notSetUp')}</Heading>
        </div>
      );
      break;
    case 'email':
      content = (
        <form onSubmit={submitEmail} noValidate>
          <Heading sub={t('signin.sub')}>{t('signin.title')}</Heading>
          <Field
            label={t('signin.workEmail')}
            name="email"
            type="email"
            inputMode="email"
            autoComplete="username"
            defaultValue={lastEmail()}
            data-desyr-email=""
            invalid={!!step.error}
            error={step.error}
          />
          <MainButton waiting={waiting} aria-label={waiting ? t('signin.finding') : undefined}>
            {t('signin.continue')}
          </MainButton>
          <p className="si-quiet-line">{t('signin.newHere')}</p>
        </form>
      );
      break;
    case 'connecting':
      content = step.failure ? (
        <div className="si-connecting">
          <ErrorLine>{step.failure}</ErrorLine>
          <MainButton type="button" onClick={() => connect(step.server)}>
            {t('common.tryAgain')}
          </MainButton>
          {anotherEmail}
        </div>
      ) : (
        <p className="si-connecting si-muted">{step.line}</p>
      );
      break;
    case 'password':
      content = (
        <form onSubmit={(e) => submitPassword(e, step)} noValidate className={busy ? 'resting' : undefined}>
          <Heading>{t('signin.title')}</Heading>
          <Field label={t('signin.username')} name="username" autoComplete="username" data-desyr-username="" />
          <Field
            label={t('signin.password')}
            name="password"
            type="password"
            reveal
            autoComplete="current-password"
            aside={forgot(step.server)}
            data-desyr-password=""
            error={error}
          />
          <MainButton waiting={waiting}>{t('signin.signIn')}</MainButton>
          {anotherEmail}
        </form>
      );
      break;
    case 'sso':
      content = (
        <div>
          <Heading>{t('signin.title')}</Heading>
          <a className="si-quiet" href={ssoUrl(step.base, `${location.origin}${location.pathname}`)}>
            {t('signin.signIn')}
          </a>
          {anotherEmail}
        </div>
      );
      break;
    case 'token':
      content = step.failure ? (
        <div>
          <Mark kind="error" />
          <Heading>{t('signin.didntWork')}</Heading>
          <ErrorLine>{step.failure}</ErrorLine>
          <MainButton type="button" onClick={() => connect(step.server)}>
            {t('start.signInAgain')}
          </MainButton>
        </div>
      ) : (
        <p className="si-connecting si-muted" />
      );
      break;
    case 'company':
      content = (
        <form onSubmit={(e) => submitCompany(e, step)} noValidate className={busy ? 'resting' : undefined}>
          <Heading>{t('signin.enterPassword')}</Heading>
          <div className="si-row" aria-label={t('signin.signingInAs')} role="group">
            <Icon svg={icons.person} size={20} />
            <span className="si-row-address">{step.email}</span>
            {discoveryOn(config) && (
              <Link
                className="si-link"
                to="/login"
                replace
                onClick={() => {
                  chooseCompany(undefined);
                  go({ name: 'email' }, true);
                }}
              >
                {t('common.change')}
              </Link>
            )}
          </div>
          <input type="email" name="username" autoComplete="username" value={step.email} readOnly hidden />
          <Field
            label={t('signin.password')}
            name="password"
            type="password"
            reveal
            autoComplete="current-password"
            aside={step.entry.admin ? forgot(step.server) : undefined}
            data-desyr-password=""
            error={error}
            invalid={!!error}
          />
          <MainButton waiting={waiting}>{t('signin.signIn')}</MainButton>
        </form>
      );
      break;
    case 'code':
      content = (
        <form onSubmit={(e) => submitCompany(e, step)} noValidate className={busy ? 'resting' : undefined}>
          <Heading sub={t('signin.codeSub')}>{t('signin.enterCode')}</Heading>
          <Field
            label={t('signin.code')}
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={7}
            data-desyr-code=""
            error={error}
            invalid={!!error}
          />
          <MainButton waiting={waiting}>{t('signin.confirm')}</MainButton>
          <Link className="si-link si-under" to={`/recover/${encodeURIComponent(step.server)}/`}>
            {t('signin.recoveryCode')}
          </Link>
        </form>
      );
      break;
    case 'error':
      content = (
        <div>
          <Mark kind="error" />
          <Heading>{t('signin.failedNow')}</Heading>
          <p className="si-muted">{t('signin.ourSide')}</p>
          <p className="si-muted">{t('signin.keepsHappening')}</p>
          {step.reference && <Reference code={step.reference} />}
          <MainButton
            type="button"
            onClick={() => {
              resetPageGuard();
              go({ name: 'company', server: step.server, base: step.base, entry: step.entry, email: step.email }, true);
            }}
          >
            {t('common.tryAgain')}
          </MainButton>
        </div>
      );
      break;
    case 'nothing':
      content = (
        <div>
          <Heading>{t('signin.title')}</Heading>
          <ErrorLine>{t('signin.nothingUsable')}</ErrorLine>
          {anotherEmail}
        </div>
      );
      break;
  }

  return (
    <Frame config={config} waiting={waiting} step={hook}>
      {adding() && (
        <div className="signin-adding">
          <h2>{t('accounts.adding')}</h2>
          <Button variant="text" small onClick={cancelAdding}>{t('common.cancel')}</Button>
        </div>
      )}
      <div ref={card}>{content}</div>
    </Frame>
  );
}
